import { CHPoint } from '../ch/point';
import { c_cities, CCityId } from '../mdl/c-city';
import { c_event_tags, CEventTagId, cEventTagName } from '../mdl/c-event-tag';
import { c_producers, CProducerId } from '../mdl/c-producer';
import { c_venues, CVenueId } from '../mdl/c-venue';
import { p_promos } from '../mdl/p-promo-admin';
import { UIValue } from '../ui/types';
import { $api } from '../y/api';
import { xavis_experiments } from '../y/debug';
import { $fn } from '../y/fn';
import { $http } from '../y/http';
import { $time, YDate, YTime } from '../y/time';
import { $id, YTimeString } from '../y/types';

export type STFilter = { [key in STParameter]?: any };

export type STOp = 'add' | 'sub' | 'mul' | 'div';

export interface STSeries {
    values: number[][];
    key: string;
    group: STFilter;

    //for calculation
    count?: number;
}

export type STMetric = 'members' | 'members_current' | 'members_active' | 'members_gift_card' | 'members_past_due' | 'members_paused' | 'action_upgrade' |
    'action_downgrade' | 'action_redeem_gc' | 'action_subscribe' | 'action_unsubscribe' | 'action_resubscribe' | 'billing_payment' | 'billing_gc' |
    'booking_booked' | 'booking_unbooked' | 'booking_used' | 'booking_checked_in' | 'event_sessions' | 'login' | 'signup' | 'sent_invites' |
    'successful_invites' | 'referrals' | 'cancellation' | 'members_not_canceling' | 'messages' | 'experiment_1' | 'experiment_2' | 'experiment_3' | 'experiment_4' | 'experiment_5' | 'experiment_6' | 'experiment_7' | 'experiment_8' | 'experiment_9' | 'experiment_10';
export type STCount = 'all' | 'unique_users' | 'unique_events' | 'unique_producers' | 'unique_venues' | 'revenue' | 'users' | 'tickets' | 'value' | 'payout' |
    'booked_value' | 'mrr' | 'tickets_booked' | 'tickets_standby' | 'occupancy';
export type STScale = 'day' | 'week' | 'month' | 'quarter' | 'year' | 'time' | 'rel_year' | 'rel_month' | 'rel_week' | 'total';
export type STParameter = 'user_status' | 'user_city' | 'user_country' | 'subscription_kind' | 'booking_quantity' | 'session_full' | 'year_cohort' | 'month_cohort' | 'week_cohort' | 'promo' |
    'user_event_tags' | 'event_tags' | 'billing_day' | 'producer' | 'venue' | 'user' | 'checked_in' | 'payment_type' | 'user_gender' |
    'user_age_group' | 'months_active' | 'trial' | 'action_code' | 'message_type';

export interface STQuery {
    scale: STScale;
    metric: STMetric;
    counts: STCount[];
    filter?: STFilter;
    group?: STParameter[];
    from?: YTime | YDate;
    to?: YTime | YDate

    pp?: {
        accumulate?: boolean;
        heavy?: boolean;
    }
}

export interface STQueryResponse {
    dates: YTimeString[];
    series: STSeries[];
    relative: boolean;
    explain: any;
    elapsed: number;
}

export interface STMetricSpec {
    name: string;
    counts: STCount[];
    parameters: STParameter[];
    when?: () => boolean;
}

export interface STParameterSpec {
    name: string;
    label?: (v: any) => string;
    when?: () => boolean;
    filter?: boolean;
}

export interface STUserPropertyName {
    name: string;
}

export interface STUserPropertyOp {
    op: string;
    args: STUserProperty[];
}

export interface STUserPropertyMetric {
    metric: STMetric;
    count: STCount;
    from?: YTime | YDate;
    to?: YTime | YDate
    filters?: STActionFilter[];
}

export type STValue = { int: number } | { float: number } | { string: string } | { time: YTime };

export interface STActionFilterOp {
    property: string;
    op: string;
    value: STValue;
}

export interface STActionFilterAnd {
    and: STActionFilter[];
}

export interface STActionFilterOr {
    or: STActionFilter[];
}

export type STActionFilter = STActionFilterOp | STActionFilterOr | STActionFilterAnd;
export type STUserProperty = STUserPropertyName | STUserPropertyOp | STUserPropertyMetric;

export interface STFunnelStep {
    action: string;
}

export interface STFunnelQuery {
    steps: STFunnelStep[];
}

export interface STPropertyQuery {
    property: STUserProperty;
}

export interface STFlowsQuery {
    depth: number;
    from?: string;
    to?: string;
    around?: string;
}

export interface STFunnelQueryResponse {
    explain: any;
    elapsed: number;
}

export interface STPropertyQueryResponse {
    explain: any;
    elapsed: number;
}

export interface STFlowsStepItem {
    event: string;
    count: number;

    next: { [event: string]: number };
}

export interface STFlowsStep {
    total: number;
    others: string[];
    items: STFlowsStepItem[];
}

export interface STFlowsQueryResponse {
    steps: STFlowsStep[];
    explain: any;
    elapsed: number;
}

export interface STSQLCol {
    name: string;
    type: string;
}

export type STSQLRow = (number | string)[];

export interface STSQLQueryResponse {
    cols: STSQLCol[];
    rows: STSQLRow[];
    elapsed: number;
}

export type STDataType = 'string' | 'number' | 'boolean' | 'date' | 'datetime' | 'time' | 'enum' | 'country' | 'city' | 'promo' | 'year' | 'month' | 'event_tag' | 'producer' | 'venue' | 'event' | 'user' | 'action' | 'plan';

export interface STParameterDescription {
    parameter: STParameter;
    name: string;
    description: string;
    data_type: STDataType;
    values: { [key: string]: string };
}

export const st_metrics: { [key in STMetric]: STMetricSpec } = {
    members: {
        name: 'Members',
        counts: ['all', 'users', 'mrr'],
        parameters: ['user_country', 'user_city', 'user_gender', 'user_age_group', 'subscription_kind', 'year_cohort', 'month_cohort', 'week_cohort', 'promo', 'user_event_tags', 'billing_day',
            'user_status', 'months_active', 'trial']
    },
    members_current: {
        name: 'Current members',
        counts: ['all', 'users', 'mrr'],
        parameters: ['user_country', 'user_city', 'user_gender', 'user_age_group', 'subscription_kind', 'year_cohort', 'month_cohort', 'week_cohort', 'promo', 'user_event_tags', 'billing_day',
            'user_status', 'months_active', 'trial']
    },
    members_active: {
        name: 'Active members',
        counts: ['all', 'users', 'mrr'],
        parameters: ['user_country', 'user_city', 'user_gender', 'user_age_group', 'subscription_kind', 'year_cohort', 'month_cohort', 'week_cohort', 'promo', 'user_event_tags', 'billing_day',
            'user_status', 'months_active', 'trial']
    },
    members_gift_card: {
        name: 'Gift card members',
        counts: ['all', 'users'],
        parameters: ['user_country', 'user_city', 'user_gender', 'user_age_group', 'subscription_kind', 'year_cohort', 'month_cohort', 'week_cohort', 'promo', 'user_event_tags', 'billing_day',
            'user_status', 'months_active', 'trial']
    },
    members_past_due: {
        name: 'Past due members',
        counts: ['all', 'users', 'mrr'],
        parameters: ['user_country', 'user_city', 'user_gender', 'user_age_group', 'subscription_kind', 'year_cohort', 'month_cohort', 'week_cohort', 'promo', 'user_event_tags', 'billing_day',
            'user_status', 'months_active', 'trial']
    },
    members_paused: {
        name: 'Paused members',
        counts: ['all', 'users', 'mrr'],
        parameters: ['user_country', 'user_city', 'user_gender', 'user_age_group', 'subscription_kind', 'year_cohort', 'month_cohort', 'week_cohort', 'promo', 'user_event_tags', 'billing_day',
            'user_status', 'months_active', 'trial']
    },
    members_not_canceling: {
        name: 'Non-canceling members',
        counts: ['all', 'users', 'mrr'],
        parameters: ['user_country', 'user_city', 'user_gender', 'user_age_group', 'subscription_kind', 'year_cohort', 'month_cohort', 'week_cohort', 'promo', 'user_event_tags', 'billing_day',
            'user_status', 'months_active', 'trial'],
    },
    action_upgrade: {
        name: 'Upgrades',
        counts: ['all'],
        parameters: ['user_country', 'user_city', 'user_gender', 'user_age_group', 'subscription_kind', 'year_cohort', 'month_cohort', 'week_cohort', 'promo', 'user_event_tags', 'billing_day',
            'user_status', 'months_active', 'trial']
    },
    action_downgrade: {
        name: 'Downgrades',
        counts: ['all'],
        parameters: ['user_country', 'user_city', 'user_gender', 'user_age_group', 'subscription_kind', 'year_cohort', 'month_cohort', 'week_cohort', 'promo', 'user_event_tags', 'billing_day',
            'user_status', 'months_active', 'trial']
    },
    action_resubscribe: {
        name: 'Resubscribes',
        counts: ['all'],
        parameters: ['user_country', 'user_city', 'user_gender', 'user_age_group', 'subscription_kind', 'year_cohort', 'month_cohort', 'week_cohort', 'promo', 'user_event_tags', 'billing_day',
            'user_status', 'months_active', 'trial']
    },
    action_subscribe: {
        name: 'New subscriptions',
        counts: ['all'],
        parameters: ['user_country', 'user_city', 'user_gender', 'user_age_group', 'subscription_kind', 'year_cohort', 'month_cohort', 'week_cohort', 'promo', 'user_event_tags', 'billing_day',
            'user_status', 'months_active', 'trial']
    },
    action_redeem_gc: {
        name: 'Gift card redeemed',
        counts: ['all'],
        parameters: ['user_country', 'user_city', 'user_gender', 'user_age_group', 'subscription_kind', 'year_cohort', 'month_cohort', 'week_cohort', 'promo', 'user_event_tags', 'billing_day',
            'user_status', 'months_active', 'trial']
    },
    action_unsubscribe: {
        name: 'Cancel requests',
        counts: ['all'],
        parameters: ['user_country', 'user_city', 'user_gender', 'user_age_group', 'subscription_kind', 'year_cohort', 'month_cohort', 'week_cohort', 'promo', 'user_event_tags', 'billing_day',
            'user_status', 'months_active', 'trial']
    },
    cancellation: {
        name: 'Cancellations',
        counts: ['all'],
        parameters: ['user_country', 'user_city', 'user_gender', 'user_age_group', 'subscription_kind', 'year_cohort', 'month_cohort', 'week_cohort', 'promo', 'user_event_tags', 'billing_day',
            'user_status']
    },
    billing_payment: {
        name: 'Payment',
        counts: ['all', 'revenue'],
        parameters: ['user_country', 'user_city', 'user_gender', 'user_age_group', 'subscription_kind', 'year_cohort', 'month_cohort', 'week_cohort', 'promo', 'user_event_tags', 'billing_day',
            'user_status', 'payment_type']
    },
    billing_gc: {
        name: 'Gift card sales',
        counts: ['all', 'revenue'],
        parameters: ['subscription_kind', 'user_country']
    },
    booking_booked: {
        name: 'Bookings booked',
        counts: ['all', 'unique_users', 'unique_events', 'unique_producers', 'unique_venues', 'tickets', 'value', 'payout'],
        parameters: ['user_country', 'user_city', 'user_gender', 'user_age_group', 'subscription_kind', 'year_cohort', 'month_cohort', 'week_cohort', 'promo', 'user_event_tags', 'billing_day',
            'user_status', 'booking_quantity', 'event_tags', 'producer', 'venue', 'user', 'checked_in', 'months_active', 'trial']
    },
    booking_unbooked: {
        name: 'Bookings unbooked',
        counts: ['all', 'unique_users', 'unique_events', 'unique_producers', 'unique_venues', 'tickets', 'value', 'payout'],
        parameters: ['user_country', 'user_city', 'user_gender', 'user_age_group', 'subscription_kind', 'year_cohort', 'month_cohort', 'week_cohort', 'promo', 'user_event_tags', 'billing_day',
            'user_status', 'booking_quantity', 'event_tags', 'producer', 'venue', 'user', 'checked_in', 'months_active', 'trial']
    },
    booking_used: {
        name: 'Bookings used',
        counts: ['all', 'unique_users', 'unique_events', 'unique_producers', 'unique_venues', 'tickets', 'value', 'payout'],
        parameters: ['user_country', 'user_city', 'user_gender', 'user_age_group', 'subscription_kind', 'year_cohort', 'month_cohort', 'week_cohort', 'promo', 'user_event_tags', 'billing_day',
            'user_status', 'booking_quantity', 'event_tags', 'producer', 'venue', 'user', 'checked_in', 'months_active', 'trial']
    },
    booking_checked_in: {
        name: 'Bookings checked in',
        counts: ['all', 'unique_users', 'unique_events', 'unique_producers', 'unique_venues', 'tickets', 'value', 'payout'],
        parameters: ['user_country', 'user_city', 'user_gender', 'user_age_group', 'subscription_kind', 'year_cohort', 'month_cohort', 'week_cohort', 'promo', 'user_event_tags', 'billing_day',
            'user_status', 'booking_quantity', 'event_tags', 'producer', 'venue', 'user', 'checked_in', 'months_active', 'trial']
    },
    login: {
        name: 'Logins',
        counts: ['all'],
        parameters: ['user_country', 'user_city', 'user_gender', 'user_age_group', 'subscription_kind', 'year_cohort', 'month_cohort', 'week_cohort', 'promo', 'user_event_tags', 'billing_day',
            'user_status']
    },
    signup: {
        name: 'Signup',
        counts: ['all'],
        parameters: ['user_country', 'user_city', 'user_gender', 'user_age_group', 'subscription_kind', 'year_cohort', 'month_cohort', 'week_cohort', 'promo', 'user_event_tags', 'billing_day',
            'user_status']
    },
    event_sessions: {
        name: 'Event sessions',
        counts: ['all', 'unique_events', 'unique_producers', 'unique_venues', 'tickets', 'tickets_booked', 'tickets_standby', 'value', 'booked_value', 'occupancy'],
        parameters: ['user_country', 'user_city', 'session_full', 'event_tags', 'producer', 'venue']
    },
    sent_invites: {
        name: 'Sent invites',
        counts: ['all'],
        parameters: ['user_country', 'user_city', 'year_cohort', 'month_cohort', 'week_cohort']
    },
    successful_invites: {
        name: 'Successful invites',
        counts: ['all'],
        parameters: ['user_country', 'user_city', 'user_gender', 'user_age_group', 'year_cohort', 'month_cohort', 'week_cohort', 'user_status']
    },
    referrals: {
        name: 'Referrals',
        counts: ['all'],
        parameters: ['user_country', 'user_city', 'user_gender', 'user_age_group', 'year_cohort', 'month_cohort', 'week_cohort', 'user_status']
    },
    messages: {
        name: 'Messages',
        counts: ['all'],
        parameters: ['action_code', 'message_type'],
        when: () => xavis_experiments
    },
    experiment_1: {
        name: 'X1 (Members)',
        counts: ['all', 'users', 'mrr'],
        parameters: ['user_country', 'user_city', 'user_gender', 'user_age_group', 'subscription_kind', 'year_cohort', 'month_cohort', 'week_cohort', 'promo', 'user_event_tags', 'billing_day',
            'user_status', 'months_active', 'trial']
    },
    experiment_2: {
        name: 'X2 (Current members)',
        counts: ['all', 'users', 'mrr'],
        parameters: ['user_country', 'user_city', 'user_gender', 'user_age_group', 'subscription_kind', 'year_cohort', 'month_cohort', 'week_cohort', 'promo', 'user_event_tags', 'billing_day',
            'user_status', 'months_active', 'trial']
    },
    experiment_3: {
        name: 'X3 (Active members)',
        counts: ['all', 'users', 'mrr'],
        parameters: ['user_country', 'user_city', 'user_gender', 'user_age_group', 'subscription_kind', 'year_cohort', 'month_cohort', 'week_cohort', 'promo', 'user_event_tags', 'billing_day',
            'user_status', 'months_active', 'trial']
    },
    experiment_4: {
        name: 'X4 (Gift card members)',
        counts: ['all', 'users'],
        parameters: ['user_country', 'user_city', 'user_gender', 'user_age_group', 'subscription_kind', 'year_cohort', 'month_cohort', 'week_cohort', 'promo', 'user_event_tags', 'billing_day',
            'user_status', 'months_active', 'trial']
    },
    experiment_5: {
        name: 'X5 (Past due members)',
        counts: ['all', 'users', 'mrr'],
        parameters: ['user_country', 'user_city', 'user_gender', 'user_age_group', 'subscription_kind', 'year_cohort', 'month_cohort', 'week_cohort', 'promo', 'user_event_tags', 'billing_day',
            'user_status', 'months_active', 'trial']
    },
    experiment_6: {
        name: 'X6 (Paused members)',
        counts: ['all', 'users', 'mrr'],
        parameters: ['user_country', 'user_city', 'user_gender', 'user_age_group', 'subscription_kind', 'year_cohort', 'month_cohort', 'week_cohort', 'promo', 'user_event_tags', 'billing_day',
            'user_status', 'months_active', 'trial']
    },
    experiment_7: {
        name: 'X7 (Cancelled members)',
        counts: ['all', 'users', 'mrr'],
        parameters: ['user_country', 'user_city', 'user_gender', 'user_age_group', 'subscription_kind', 'year_cohort', 'month_cohort', 'week_cohort', 'promo', 'user_event_tags', 'billing_day',
            'user_status', 'months_active', 'trial']
    },
    experiment_8: {
        name: 'X8 (Non-canceling members)',
        counts: ['all', 'users', 'mrr'],
        parameters: ['user_country', 'user_city', 'user_gender', 'user_age_group', 'subscription_kind', 'year_cohort', 'month_cohort', 'week_cohort', 'promo', 'user_event_tags', 'billing_day',
            'user_status', 'months_active', 'trial'],
    },
    experiment_9: {
        name: 'X9 (Cancellations)',
        counts: ['all'],
        parameters: ['action_code', 'message_type']
    },
    experiment_10: {
        name: 'X10 (Sign ups)',
        counts: ['all'],
        parameters: ['user_country', 'user_city', 'user_gender', 'user_age_group', 'subscription_kind', 'year_cohort', 'month_cohort', 'week_cohort', 'promo', 'user_event_tags', 'billing_day',
            'user_status']
    },
};

export const st_parameters: { [key in STParameter]: STParameterSpec } = {
    user_country: {
        name: 'Country',
        label: v => {
            return v == 'DE' ? 'Germany' : v == 'SE' ? 'Sweden' : '-';
        },
        filter: true
    },
    user_city: {
        name: 'City',
        label: v => {
            const city = c_cities.maybe($id.objectIdToId(v) as CCityId);
            return city ? city.name : '-';
        },
        filter: true
    },
    user_gender: {
        name: 'Gender',
        label: v => v || '-',
        filter: true
    },
    user_age_group: {
        name: 'Age group',
        label: v => v || '-'
    },
    subscription_kind: {
        name: 'Subscription kind',
        label: v => v,
        filter: true
    },
    user_status: {
        name: 'Current status',
        label: v => v,
        filter: true
    },
    booking_quantity: {
        name: 'Ticket quantity',
        label: v => '' + v
    },
    session_full: {
        name: 'Fully booked',
        label: v => '' + v
    },
    year_cohort: {
        name: 'Year cohort',
        when: () => false
    },
    month_cohort: {
        name: 'Month cohort',
        when: () => false
    },
    week_cohort: {
        name: 'Week cohort',
        when: () => false
    },
    promo: {
        name: 'Promo',
        label: id => {
            const promo = p_promos.maybe($id.objectIdToId(id));
            return promo ? promo.name : 'Unknown';
        }
    },
    user_event_tags: {
        name: 'User genres',
        label: id => {
            if(id == '')
                return 'None';

            const tag = c_event_tags.maybe($id.objectIdToId(id) as CEventTagId);
            return tag ? cEventTagName(tag) : '-';
        }
    },
    event_tags: {
        name: 'Event genres',
        label: id => {
            if(id == '')
                return 'None';

            const tag = c_event_tags.maybe($id.objectIdToId(id) as CEventTagId);
            return tag ? cEventTagName(tag) : '-';
        }
    },
    billing_day: {
        name: 'Billing day',
        label: v => v
    },
    producer: {
        name: 'Producer',
        label: id => {
            const producer = c_producers.maybe($id.objectIdToId(id) as CProducerId)
            return producer?.name ?? '-';
        }
    },
    venue: {
        name: 'Venue',
        label: id => {
            const venue = c_venues.maybe($id.objectIdToId(id) as CVenueId)
            return venue?.name ?? '-';
        }
    },
    user: {
        name: 'User',
        when: () => false
    },
    checked_in: {
        name: 'Checked in',
        label: v => v ? 'Yes' : 'No',
    },
    payment_type: {
        name: 'Payment type',
        label: v => v,
    },
    months_active: {
        name: 'Months active',
        label: v => v,
    },
    trial: {
        name: 'Trial',
        label: v => v ? 'Trial' : 'No trial'
    },
    action_code: {
        name: 'Action code',
        label: v => v,
        when: () => xavis_experiments,
    },
    message_type: {
        name: 'Message type',
        label: v => v,
        when: () => xavis_experiments,
    }
};

export const st_counts: { [key in STCount]: string } = {
    all: 'Count',
    unique_users: 'Unique users',
    unique_events: 'Unique events',
    unique_producers: 'Unique producers',
    unique_venues: 'Unique venues',
    revenue: 'Revenue',
    users: 'Users',
    tickets: 'Tickets',
    value: 'Ticket value',
    payout: 'Payout value',
    booked_value: 'Booked value',
    mrr: 'MRR',
    tickets_booked: 'Booked tickets',
    tickets_standby: 'Standby tickets',
    occupancy: 'Average occupancy',
};

function opValues(op: STOp, as: number[], bs: number[]) {
    const len = Math.min(as.length, bs.length);
    const ret = new Array(len);

    switch(op) {
        case 'div':
            for(let i = 0; i < len; i++) {
                ret[i] = bs[i] ? as[i] / bs[i] : 0;
            }
            break;

        case 'add':
            for(let i = 0; i < len; i++) {
                ret[i] = as[i] + bs[i];
            }
            break;

        case 'sub':
            for(let i = 0; i < len; i++) {
                ret[i] = as[i] - bs[i];
            }
            break;

        case 'mul':
            for(let i = 0; i < len; i++) {
                ret[i] = as[i] * bs[i];
            }
            break;
    }

    return ret;
}

function sumValues(s: number[]) {
    let sum = 0;

    for(const v of s) {
        sum += v;
    }

    return sum;
}

export class STBundle {
    readonly dates: YTimeString[];
    readonly series: STSeries[];
    readonly metric: STMetric;
    readonly count: STCount;
    readonly group_by: STParameter[];
    private readonly _counts: STCount[];
    readonly scale: STScale;

    constructor(dates: YTimeString[], series: STSeries[], metric: STMetric, count: STCount, group_by: STParameter[], counts: STCount[], scale: STScale) {
        this.dates = dates;
        this.series = series;
        this.metric = metric;
        this.count = count;
        this.group_by = group_by;
        this._counts = counts;
        this.scale = scale;
    }

    flatten(parameter: STParameter) {
        const group_by = this.group_by.filter(k => k != parameter);

        const grouped: { [key: string]: STSeries } = {};

        for(const s of this.series) {
            const key = group_by.map(k => s.group[k]).join('|||');
            if(grouped[key]) {
                const g = grouped[key];
                for(let i = 0; i < g.values.length; i++) {
                    g.values[i] = opValues('add', g.values[i], s.values[i]);
                }
            } else {
                const group: STFilter = {}
                for(const by of group_by)
                    group[by] = s.group[by];

                grouped[key] = {
                    values: Array.from(s.values),
                    key: key,
                    group: group
                };
            }
        }

        const ret: STSeries[] = [];
        for(const k in grouped) {
            ret.push(grouped[k])
        }

        return new STBundle(this.dates, ret, this.metric, this.count, group_by, this._counts, this.scale);
    }

    filter(parameter: STParameter, value: string | number | undefined) {
        if(value === undefined)
            return this.flatten(parameter);

        const group_by = this.group_by.filter(k => k != parameter);
        const filtered = this.series.filter(s => s.group[parameter] === value);

        return new STBundle(this.dates, filtered, this.metric, this.count, group_by, this._counts, this.scale);
    }

    collapse(filter: STFilter) {
        const group_by = this.group_by.filter(k => !(k in filter));

        const grouped: { [key: string]: STSeries } = {};

        const actual_filter: { parameter: STParameter, value: any }[] = [];
        for(const p in filter) {
            const v = filter[p as STParameter];
            if(v !== undefined) {
                actual_filter.push({ parameter: p as STParameter, value: v })
            }
        }

        for(const s of this.series) {
            let pass = true;
            for(const f of actual_filter) {
                if(s.group[f.parameter] !== f.value) {
                    pass = false;
                    break;
                }
            }
            if(!pass) continue;
            const key = group_by.map(k => s.group[k]).join('|||');
            if(grouped[key]) {
                const g = grouped[key];
                for(let i = 0; i < g.values.length; i++) {
                    g.values[i] = opValues('add', g.values[i], s.values[i]);
                }
            } else {
                const group: STFilter = {}
                for(const by of group_by)
                    group[by] = s.group[by];

                grouped[key] = {
                    values: Array.from(s.values),
                    key: key,
                    group: group
                };
            }
        }

        const ret: STSeries[] = [];
        for(const k in grouped) {
            ret.push(grouped[k])
        }

        return new STBundle(this.dates, ret, this.metric, this.count, group_by, this._counts, this.scale);
    }

    sum(s_i: number, c_i: number) {
        if(!this.series[s_i])
            return 0;
        return sumValues(this.series[s_i].values[c_i]);
    }

    op(op: STOp, rhs: STBundle) {
        const rhs_s = rhs.series;

        const ret_s = this.series.map(s => {
            for(const t of rhs_s) {
                if(t.key == s.key) {
                    return {
                        values: s.values.map((v, i) => opValues(op, v, t.values[i])),
                        key: s.key,
                        group: s.group
                    }
                }
            }

            return s;
        });

        return new STBundle(this.dates, ret_s, this.metric, this.count, this.group_by, this._counts, this.scale);
    }

    tail(i: number) {
        const series = this.series;

        if(series.length <= 8) return this;
        let total = 0;

        for(const s of series) {
            s.count = sumValues(s.values[i]);
            total += s.count;
        }

        series.sort((a, b) => b.count! - a.count!);

        const ret = series.slice(0, 6);
        let n: number;
        for(n = 6; n < 10 && n < series.length; n++) {
            if(series[n].count! > 0.01 * total)
                ret.push(series[n])
            else
                break;
        }

        if(n < series.length) {
            const other: STSeries = {
                values: Array.from(series[n].values),
                key: 'other',
                group: {}
            }
            for(n = n + 1; n < series.length; n++) {
                other.values[i] = opValues('add', other.values[i], series[n].values[i]);
            }

            ret.push(other);
        }

        return new STBundle(this.dates, ret, this.metric, this.count, this.group_by, this._counts, this.scale);
    }

    linePoints(s_i: number, c_i: number, from?: YTime | YDate, to?: YTime | YDate) {
        const data: CHPoint<number, number>[] = [];
        const dates = this.dates;

        if(!this.series[s_i]) return [];

        if(dates.length) {
            const from_n = from ? from.n : -Infinity;
            const to_n = to ? to.n : Infinity;

            const values = this.series[s_i].values[c_i];
            for(let j = 0; j < dates.length; j++) {
                const n = $time(dates[j]).n;
                if(n < from_n || n > to_n) continue;
                data.push({ x: n, y: values[j] });
            }
        }

        return data;
    }

    columnPoints(s_i: number, c_i: number, format: string = 'MMM yy') {
        const series = this.series[s_i];
        return this.dates.map((d, i) => ({ x: $time(d).format(format), y: series.values[c_i][i] }));
    }
}

export function stQuery(query: STQuery) {
    return $http.POST<STQueryResponse>('/api/v1/statistics/query', query).then(response => {
        console.log('Query: ', response.explain);
        console.log('Elapsed:', response.elapsed.toFixed(2) + 's');
        return new STBundle(response.dates, response.series, query.metric, query.counts[0] ?? 'all', query.group || [], query.counts, query.scale);
    });
}

export function stQueryProperty(query: STPropertyQuery) {
    return $http.POST<STPropertyQueryResponse>('/api/v1/statistics/property', query).then(response => {
        console.log('Query: ', response.explain);
        console.log('Elapsed:', response.elapsed.toFixed(2) + 's');
        console.log('Response:', response);
        return response;
    });
}

export function stQueryFunnel(query: STFunnelQuery) {
    return $http.POST<STFunnelQueryResponse>('/api/v1/statistics/funnel', query).then(response => {
        console.log('Query: ', response.explain);
        console.log('Elapsed:', response.elapsed.toFixed(2) + 's');
        console.log('Response:', response);
        return response;
    });
}

export function stQueryFlows(query: STFlowsQuery) {
    return $http.POST<STFlowsQueryResponse>('/api/v1/statistics/flows', query).then(response => {
        console.log('Query: ', response.explain);
        console.log('Elapsed:', response.elapsed.toFixed(2) + 's');
        console.log('Steps:', response.steps);
        return response;
    });
}

export function stQuerySQL(query: string) {
    return $http.POST<STSQLQueryResponse>('/api/v1/statistics/sql', { query: query }).then(response => {
        // console.log('Query: ', JSON.stringify(response.explain, undefined, 4));
        // console.log('Elapsed:', response.elapsed.toFixed(2) + 's');
        // console.log('Response:', response);
        return response;
    });
}

interface STParametersResponse {
    user_parameters: STParameterDescription[];
    action_parameters: STParameterDescription[];
    values: { [key in STDataType]: UIValue<string>[] }
}

export const stGetParameters = $fn.lazy(() => $api.GET<STParametersResponse>('statistics/parameters'));
